import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';

const THREAT_LEVEL_OPTIONS = [
  { value: 1, label: 'Molt Baix', color: 'bg-green-500', hoverColor: 'hover:bg-green-400' },
  { value: 2, label: 'Baix', color: 'bg-green-400', hoverColor: 'hover:bg-green-300' },
  { value: 3, label: 'Mitjà-Baix', color: 'bg-yellow-400', hoverColor: 'hover:bg-yellow-300' },
  { value: 4, label: 'Mitjà', color: 'bg-yellow-500', hoverColor: 'hover:bg-yellow-400' },
  { value: 5, label: 'Mitjà-Alt', color: 'bg-orange-400', hoverColor: 'hover:bg-orange-300' },
  { value: 6, label: 'Alt', color: 'bg-orange-500', hoverColor: 'hover:bg-orange-400' },
  { value: 7, label: 'Molt Alt', color: 'bg-red-400', hoverColor: 'hover:bg-red-300' },
  { value: 8, label: 'Crític', color: 'bg-red-500', hoverColor: 'hover:bg-red-400' },
  { value: 9, label: 'Sever', color: 'bg-red-600', hoverColor: 'hover:bg-red-500' },
  { value: 10, label: 'Extrem', color: 'bg-red-700', hoverColor: 'hover:bg-red-600' }
];

const CONFIDENCE_OPTIONS = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Mitjana' },
  { value: 'high', label: 'Alta' }
];

// ACH cell value scores (disconfirmation method)
const CELL_SCORES = { 'CC': 0, 'C': 0, 'N': 0, 'I': -1, 'II': -2 };

function ThreatAssessmentForm({ assessment, actors, hypotheses, evidence, achCells, onSave, onFinalSubmit, isReadOnly }) {
  const [formData, setFormData] = useState({
    threatLevel: null,
    confidence: '',
    summary: '',
    recommendations: '',
    mostLikelyHypothesisId: null
  });
  const [errors, setErrors] = useState({});
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  // 'net' | 'pendent' | 'desant' | 'desat' | 'error'
  const [estatDesat, setEstatDesat] = useState('net');
  const hiHaCanvisSenseDesar = useRef(false);

  // Calculate best hypothesis based on ACH scores (least negative = best)
  const bestHypothesis = useMemo(() => {
    if (!hypotheses || hypotheses.length === 0) return null;
    if (!achCells || achCells.length === 0) return hypotheses[0]; // If no cells, return first

    // Create cell lookup map
    const cellMap = {};
    achCells.forEach(cell => {
      cellMap[`${cell.hypothesisId}-${cell.evidenceId}`] = cell;
    });

    // Calculate score for each hypothesis (sum of cell scores)
    const hypothesesWithScores = hypotheses.map(h => {
      let score = 0;
      if (evidence && evidence.length > 0) {
        evidence.forEach(e => {
          const cell = cellMap[`${h.id}-${e.id}`];
          if (cell && cell.rating && CELL_SCORES[cell.rating] !== undefined) {
            score += CELL_SCORES[cell.rating];
          }
        });
      }
      return { ...h, calculatedScore: score };
    });

    // Sort by score descending (least negative = best)
    const sorted = hypothesesWithScores.sort((a, b) => b.calculatedScore - a.calculatedScore);
    return sorted[0];
  }, [hypotheses, achCells, evidence]);

  useEffect(() => {
    if (assessment) {
      setFormData({
        threatLevel: assessment.overallScore || null,
        confidence: assessment.confidenceLevel || '',
        summary: assessment.executiveSummary || '',
        recommendations: assessment.recommendations || '',
        mostLikelyHypothesisId: assessment.mostLikelyHypothesisId || null
      });
    }
  }, [assessment]);

  // Auto-suggest best hypothesis if none selected
  useEffect(() => {
    if (!formData.mostLikelyHypothesisId && bestHypothesis && !isReadOnly) {
      setFormData(prev => ({ ...prev, mostLikelyHypothesisId: bestHypothesis.id }));
    }
  }, [bestHypothesis, formData.mostLikelyHypothesisId, isReadOnly]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    hiHaCanvisSenseDesar.current = true;
    setEstatDesat('pendent');
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.threatLevel) {
      newErrors.threatLevel = "El nivell d'amenaça és obligatori";
    }
    if (!formData.summary?.trim()) {
      newErrors.summary = "L'avaluació de l'amenaça és obligatòria";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = useCallback(async () => {
    setEstatDesat('desant');
    try {
      await onSave({
        overallScore: formData.threatLevel,
        executiveSummary: formData.summary,
        recommendations: formData.recommendations || null,
        confidenceLevel: formData.confidence || null,
        mostLikelyHypothesisId: formData.mostLikelyHypothesisId || null
      });
      hiHaCanvisSenseDesar.current = false;
      setEstatDesat('desat');
    } catch (err) {
      // El workspace ja mostra l'error i desfà l'actualització optimista. Aquí es
      // marca 'error' i no 'pendent' expressament: l'auto-desat només s'engega amb
      // 'pendent', així una fallada persistent no queda reintentant cada tres segons
      // contra un servidor que ja ha dit que no. Torna a provar-ho quan l'usuari
      // escrigui alguna cosa més, o quan premi «Guardar Esborrany» a mà.
      setEstatDesat('error');
    }
  }, [formData, onSave]);

  // Auto-desat.
  //
  // Fins al 07/08/2026 aquest formulari només persistia quan es premia «Guardar
  // Esborrany». Si s'acabava el temps abans, l'exercici passava a només lectura i el
  // resum executiu i les recomanacions que l'alumne acabava d'escriure es perdien.
  // Era el retret més repetit de qui el va fer servir, i el disseny (§9) ja
  // demanava auto-desat, que mai es va arribar a implementar.
  //
  // La resta del workspace no ho necessita: les cel·les de l'ACH i els actors ja
  // fan una petició per cada acció. Aquí no, perquè és text lliure.
  //
  // Tres segons d'inactivitat: prou per no fer una petició per tecla, prou poc per
  // no perdre gairebé res si el navegador es tanca de cop.
  useEffect(() => {
    if (isReadOnly || estatDesat !== 'pendent') return undefined;
    const t = setTimeout(() => { handleSave(); }, 3000);
    return () => clearTimeout(t);
  }, [isReadOnly, estatDesat, handleSave]);

  // Xarxa de seguretat per si el navegador es tanca amb canvis pendents.
  useEffect(() => {
    const avisar = (e) => {
      if (!hiHaCanvisSenseDesar.current || isReadOnly) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [isReadOnly]);

  const handleSubmit = () => {
    if (validate()) {
      setShowSubmitModal(true);
    }
  };

  const confirmSubmit = async () => {
    // S'espera el desat: si no, l'enviament podia arribar abans que el text.
    await handleSave();
    setShowSubmitModal(false);
    onFinalSubmit();
  };

  const getThreatColor = () => {
    if (!formData.threatLevel) return 'bg-surface-muted';
    const level = THREAT_LEVEL_OPTIONS.find(l => l.value === formData.threatLevel);
    return level?.color || 'bg-surface-muted';
  };

  const selectedHypothesis = hypotheses?.find(h => h.id === formData.mostLikelyHypothesisId);

  return (
    <div className="h-full flex flex-col bg-surface">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-content flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Avaluació Final d'Amenaça
          </h3>
          {formData.threatLevel && (
            <div className={`px-4 py-2 rounded-full ${getThreatColor()} text-white font-bold text-lg`}>
              {formData.threatLevel}/10
            </div>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">

        {/* 1. Threat Level - Buttons with number and label */}
        <div>
          <label className="block text-sm font-medium text-content-secondary mb-3">
            Nivell d'Amenaça Global *
          </label>
          <div className="grid grid-cols-5 gap-2">
            {THREAT_LEVEL_OPTIONS.map(level => (
              <button
                key={level.value}
                type="button"
                onClick={() => !isReadOnly && handleChange('threatLevel', level.value)}
                disabled={isReadOnly}
                className={`py-2 px-1 rounded-lg transition-all flex flex-col items-center justify-center ${
                  formData.threatLevel === level.value
                    ? `${level.color} text-white ring-2 ring-offset-1 ring-content-disabled`
                    : `bg-surface-muted text-content-secondary ${!isReadOnly ? 'hover:bg-surface-muted' : ''}`
                } ${isReadOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
              >
                <span className="text-lg font-bold">{level.value}</span>
                <span className="text-[10px] leading-tight">{level.label}</span>
              </button>
            ))}
          </div>
          {errors.threatLevel && (
            <p className="mt-2 text-sm text-error">{errors.threatLevel}</p>
          )}
        </div>

        {/* 2. Main Hypothesis */}
        {hypotheses && hypotheses.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-content-secondary mb-2">
              Hipòtesi Principal
              {bestHypothesis && formData.mostLikelyHypothesisId === bestHypothesis.id && (
                <span className="ml-2 text-xs font-normal text-accent">
                  (suggerida per ACH)
                </span>
              )}
            </label>
            <select
              value={formData.mostLikelyHypothesisId || ''}
              onChange={(e) => handleChange('mostLikelyHypothesisId', e.target.value || null)}
              disabled={isReadOnly}
              className={`w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-surface text-content ${isReadOnly ? 'bg-surface-muted cursor-not-allowed' : ''}`}
            >
              <option value="">Seleccionar hipòtesi...</option>
              {hypotheses.map(h => (
                <option key={h.id} value={h.id}>
                  {h.code} - {h.description}
                  {h.id === bestHypothesis?.id ? ' ★' : ''}
                </option>
              ))}
            </select>
            {selectedHypothesis && (
              <p className="mt-2 text-sm text-content-secondary bg-surface-subtle p-2 rounded">
                <span className="font-medium text-accent">{selectedHypothesis.code}:</span>{' '}
                {selectedHypothesis.description}
              </p>
            )}
          </div>
        )}

        {/* 3. Assessment Text - Main focus */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-content-secondary mb-2">
            Avaluació de l'Amenaça *
          </label>
          <textarea
            value={formData.summary}
            onChange={(e) => handleChange('summary', e.target.value)}
            disabled={isReadOnly}
            rows={10}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-surface text-content placeholder-content-disabled text-base leading-relaxed ${
              errors.summary ? 'border-error/30' : 'border-border-strong'
            } ${isReadOnly ? 'bg-surface-muted cursor-not-allowed' : ''}`}
            placeholder="Descriu la teva avaluació de l'amenaça basant-te en l'anàlisi dels actors, les evidències i les hipòtesis...

Considera:
• Quins actors representen la major amenaça i per què
• Com es relacionen les evidències amb les hipòtesis
• Quin és l'escenari més probable
• Quins factors podrien agreujar o mitigar l'amenaça"
          />
          {errors.summary && (
            <p className="mt-1 text-sm text-error">{errors.summary}</p>
          )}
        </div>

        {/* 4. Optional Fields */}
        <div className="border-t border-border pt-6 space-y-4">
          {/* Confidence - Inline compact */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-content-secondary">
              Nivell de Confiança <span className="font-normal text-content-disabled">(opcional)</span>
            </label>
            <div className="flex items-center space-x-1">
              {CONFIDENCE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => !isReadOnly && handleChange('confidence', formData.confidence === opt.value ? '' : opt.value)}
                  disabled={isReadOnly}
                  className={`px-3 py-1 text-xs rounded-full transition-all ${
                    formData.confidence === opt.value
                      ? 'bg-accent text-white'
                      : 'bg-surface-muted text-content-secondary hover:bg-surface-muted'
                  } ${isReadOnly ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div>
            <label className="block text-sm font-medium text-content-secondary mb-2">
              Recomanacions <span className="font-normal text-content-disabled">(opcional)</span>
            </label>
            <textarea
              value={formData.recommendations}
              onChange={(e) => handleChange('recommendations', e.target.value)}
              disabled={isReadOnly}
              rows={3}
              className={`w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-surface text-content placeholder-content-disabled text-sm ${isReadOnly ? 'bg-surface-muted cursor-not-allowed' : ''}`}
              placeholder="Mesures de mitigació, accions recomanades..."
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      {!isReadOnly && (
        <div className="p-4 border-t border-border bg-surface-subtle flex justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm text-content-secondary border border-border-strong rounded-lg hover:bg-surface transition-colors"
            >
              Guardar Esborrany
            </button>
            {/* L'alumne ha de poder saber si el que ha escrit és a lloc segur. */}
            <span className="text-xs text-content-muted">
              {estatDesat === 'desant' && 'Desant…'}
              {estatDesat === 'desat' && 'Canvis desats'}
              {estatDesat === 'pendent' && 'Canvis sense desar…'}
              {estatDesat === 'error' && (
                <span className="text-error">No s'han pogut desar</span>
              )}
            </span>
          </div>
          <button
            onClick={handleSubmit}
            className="px-6 py-2 text-sm bg-accent text-white rounded-lg hover:bg-accent-hover transition-colors flex items-center font-medium"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Enviar Exercici
          </button>
        </div>
      )}

      {/* Read-only indicator */}
      {isReadOnly && (
        <div className="p-4 border-t border-border bg-surface-muted text-center">
          <p className="text-sm text-content-muted flex items-center justify-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Avaluació en mode només lectura
          </p>
        </div>
      )}

      {/* Submit Confirmation Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-surface rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-4 border-b border-border">
              <h3 className="text-lg font-semibold text-content flex items-center">
                <svg className="w-5 h-5 mr-2 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Confirmar Enviament
              </h3>
            </div>
            <div className="p-4">
              <div className="mb-4 p-4 bg-surface-subtle rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-content-secondary">Nivell d'Amenaça:</span>
                  <span className={`px-3 py-1 rounded-full ${getThreatColor()} text-white font-bold`}>
                    {formData.threatLevel}/10 - {THREAT_LEVEL_OPTIONS.find(l => l.value === formData.threatLevel)?.label}
                  </span>
                </div>
                {selectedHypothesis && (
                  <div className="flex items-center justify-between">
                    <span className="text-content-secondary">Hipòtesi:</span>
                    <span className="font-medium text-content">{selectedHypothesis.code}</span>
                  </div>
                )}
                {formData.confidence && (
                  <div className="flex items-center justify-between">
                    <span className="text-content-secondary">Confiança:</span>
                    <span className="font-medium text-content">
                      {CONFIDENCE_OPTIONS.find(c => c.value === formData.confidence)?.label}
                    </span>
                  </div>
                )}
              </div>

              <div className="p-3 bg-warning-subtle border border-warning/30 rounded-lg text-sm text-warning-text">
                <p className="font-semibold mb-1">Atenció:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>L'exercici quedarà en mode només lectura</li>
                  <li>No es podrà modificar després d'enviar</li>
                </ul>
              </div>
            </div>
            <div className="p-4 border-t border-border flex justify-end space-x-3">
              <button
                onClick={() => setShowSubmitModal(false)}
                className="px-4 py-2 text-sm text-content-secondary border border-border-strong rounded-lg hover:bg-surface-subtle"
              >
                Cancel·lar
              </button>
              <button
                onClick={confirmSubmit}
                className="px-4 py-2 text-sm bg-accent text-white rounded-lg hover:bg-accent-hover font-medium"
              >
                Confirmar i Enviar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ThreatAssessmentForm;
